import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { loginSuccess } from "./UserSlice";
import { isMobile } from "react-device-detect";

const VerifyOtp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [status, setStatus] = useState(false);
  const [userData, setUserData] = useState(null);
  const [timer, setTimer] = useState(60);
  const [resendDisabled, setResendDisabled] = useState(true);

  const location = useLocation();
  const user = location.state?.user || null;
  const { id } = useParams();
  const apiOrigin = process.env.REACT_APP_API_ORIGIN || "";
  const apiBase = `${apiOrigin.replace(/\/$/, "")}/${id}/v3`;
  const schoolname = localStorage.getItem("schoolname");

  // ⏳ Countdown Timer for Resend OTP
  useEffect(() => {
    if (resendDisabled && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setResendDisabled(false);
    }
  }, [timer, resendDisabled]);

  // 🚀 Load User Data
  useEffect(() => {
    if (!user) {
      toast.error("No token found! Please log in again.");
      navigate("/" + id);
      return;
    }

    try {
      console.log("Decoded Token:", user);
      setUserData(user); // Save decoded user data
    } catch (error) {
      toast.error("Invalid token! Please log in again.");
      navigate("/" + id);
    }
  }, [navigate]);

  // ✅ Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${apiBase}/verify-otp`, {
        email: user?.email,
        otp,
      });

      if (response.data.status === "success") {
        toast.success("OTP Verified! Redirecting...");

        const token = response.data.token;
        const decodedUser = jwtDecode(token); // 🔹 Decode JWT token

        // ✅ Save token & user data in Redux
        dispatch(loginSuccess({ token, user: decodedUser }));

        setTimeout(() => {
          navigate("/" + id + "/dashboard");
        }, 2000);
      } else {
        throw new Error("Invalid OTP");
      }
    } catch (error) {
      toast.error("Invalid OTP. Please try again.");
    }
  };

  // 🔄 Resend OTP
  const handleResendOtp = async () => {
    setResendDisabled(true);
    setTimer(60); // Reset timer to 60 seconds

    try {
      const response = await axios.post(`${apiBase}/resend-otp`, {
        email: userData?.email,
      });

      if (response.data.message === "OTP sent successfully") {
        toast.success("New OTP has been sent to your email.");
      } else {
        throw new Error("Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Error resending OTP. Please try again.");
      setResendDisabled(false);
    }
  };

  return (
    <div className="d-lg-flex min-vh-100">
      {/* Left - OTP Form Section */}
      <div className="col-md-6 d-flex flex-column px-6 px-lg-5 py-lg-4 py-4 mt-n2 mt-lg-o bg-white position-relative">
        {/* 🔹 Logo and Name */}
        <div className="mb-5 d-flex align-items-center mb-8 ms-n4 ms-lg-0">
          <span className="fs-t2 fw-medium text-dark">{schoolname}</span>
        </div>

        {/* 🔹 Center the OTP form vertically */}
        <div
          className="flex-grow-1 d-flex flex-column justify-content-center"
          style={{ maxWidth: "400px", margin: "0 auto", width: "100%" }}
        >
          <div className="text-dark">
            <div className="mb-4 fw-medium fs-t text-purple">Verify OTP</div>
            <div className="mb-4 fs-4">
              Enter the OTP sent to your email: <b>{userData?.email}</b>
            </div>

            <form onSubmit={handleVerifyOtp}>
              <input
                type="number"
                className="form-control form-control-lg text-dark fs-3 rounded-pill text-center my-4"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />

              <button
                className="btn btn-purple btn-lg w-100 rounded-pill mb-3"
                type="submit"
                disabled={status}
              >
                {status ? "Verifying..." : "Verify OTP"}
              </button>
            </form>

            {/* 🔄 Resend Section */}
            <div className="text-center mt-4">
              {resendDisabled ? (
                <p className="text-danger fs-5">
                  Resend OTP in <b>{timer}s</b>
                </p>
              ) : (
                <button
                  className="btn btn-outline-dark rounded-4 px-5 fw-medium"
                  onClick={handleResendOtp}
                  disabled={resendDisabled}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 🔻 Footer */}
        <div className="mt-5 text-center">
          <img
            src="../assets/smlogo.png"
            width={isMobile ? "76" : "80"}
            alt="logo"
            className="mb-3"
          />
          <div className="text-purple2 fs-4">
            © 2022 Powered and developed with ❤️. All rights reserved
          </div>
        </div>
      </div>

      {/* Right - Illustration Section */}
      <div className="col-md-6 d-flex flex-column justify-content-center align-items-center bg-purple4 text-white px-6 px-lg-5 py-lg-4 py-6">
        <div className="text-center">
          <img
            src="../assets/images/background/secure.png"
            alt="Analytics"
            className="img-fluid mb-4"
            width={"100%"}
          />
          <div className="text-white fs-3 fw-medium mb-3">
            Fast, Easy, and Secure
          </div>
          <p className="text-white fs-4 mb-0">
            You’re just one step away from full access. <br /> Enter the code
            and
            <b> verify instantly!</b>
          </p>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default VerifyOtp;
